/**
 * Cookie consent banner — first-pass GDPR/Vietnam Decree-13 friendly.
 *
 * Shows when there's no `eql_cookie_consent` cookie. User clicks
 * "Accept" → cookie set to `accepted` (1 year), banner disappears,
 * analytics scripts in root.tsx start rendering on next navigation.
 * Click "Reject" → cookie set to `rejected`, banner disappears,
 * analytics stays off.
 *
 * One-cookie design (not granular per category — that's a v2 with a
 * preferences modal). Adequate for an initial GDPR-compliant launch
 * because all of our analytics tags are non-essential and can be
 * cleanly toggled off as a group.
 *
 * The banner is a client-side `useState` because we don't want a flash
 * of "no banner" before hydration on visitors who already accepted.
 * Hydration timing means the banner only appears after first render
 * if the cookie isn't set — good UX trade-off.
 *
 * Audit 2026-05-06 (compliance, item #12).
 */
import { useEffect, useState } from "react";
import type { Locale } from "~/utils/locale";

const COOKIE_NAME = "eql_cookie_consent";

function readConsent(): "accepted" | "rejected" | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]+)"),
  );
  if (!match) return null;
  const v = match[1];
  return v === "accepted" || v === "rejected" ? v : null;
}

function writeConsent(value: "accepted" | "rejected") {
  // 1-year expiry; SameSite=Lax so navigations across sites don't lose it.
  // Not HttpOnly because client-side JS gates analytics on this read.
  // Not Secure on localhost dev; in prod the browser only persists Secure
  // cookies on https — so we always set Secure when on https origin.
  if (typeof document === "undefined") return;
  const isHttps = window.location.protocol === "https:";
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie =
    `${COOKIE_NAME}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax` +
    (isHttps ? "; Secure" : "");
  // Reload so analytics scripts start (or stay off) cleanly. Lighter
  // than rebooting React's render tree to inject scripts mid-session.
  window.location.reload();
}

interface Props {
  locale: Locale;
}

export function CookieConsent({ locale }: Props) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (readConsent() === null) setShow(true);
  }, []);

  if (!show) return null;

  const isVi = locale === "vi";

  return (
    <div
      role="dialog"
      aria-label={isVi ? "Thông báo cookie" : "Cookie notice"}
      className="fixed bottom-0 left-0 right-0 z-[120] bg-white border-t border-[#e0e0e0] shadow-lg"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <p className="text-xs text-luxury-stone leading-relaxed flex-1">
          {isVi
            ? "Chúng tôi sử dụng cookie để phân tích lưu lượng và cải thiện trải nghiệm. Cookie cần thiết luôn được bật. Bạn có thể chấp nhận hoặc từ chối cookie phân tích / quảng cáo."
            : "We use cookies to analyze traffic and improve your experience. Essential cookies are always on. You can accept or reject analytics / advertising cookies."}
          {" "}
          <a
            href="/privacy"
            className="underline hover:text-luxury-charcoal whitespace-nowrap"
          >
            {isVi ? "Xem chính sách" : "Learn more"}
          </a>
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => writeConsent("rejected")}
            className="text-xs uppercase tracking-wider px-4 py-2 border border-[#e0e0e0] hover:border-black transition-colors"
          >
            {isVi ? "Từ chối" : "Reject"}
          </button>
          <button
            type="button"
            onClick={() => writeConsent("accepted")}
            className="text-xs uppercase tracking-wider px-4 py-2 bg-black text-white hover:bg-luxury-charcoal transition-colors"
          >
            {isVi ? "Chấp nhận" : "Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}
